import { createSlice } from "@reduxjs/toolkit";
import {
  addSaga,
  createAsyncSaga,
  createSagaReducer,
  sagaSlice,
} from "./sagaStore";
import FetchShopSubscriptionService from "../services/fetchShopSubscription";
import FetchSubscriptionInvoicesService from "../services/fetchSubscriptionInvoices";
import type {
  ShopSubscriptionResponse,
  SubscriptionInvoicesResponse,
} from "../types/subscription";

export interface ShopSubscriptionState {
  detail: {
    loading: boolean;
    error: string | null;
    /** Full API envelope — unwrap via `result.data`. */
    data: ShopSubscriptionResponse | Record<string, unknown>;
  };
  invoices: {
    loading: boolean;
    error: string | null;
    /** Full API envelope — unwrap via `result.data`. */
    data: SubscriptionInvoicesResponse | Record<string, unknown>;
  };
}

const initialState: ShopSubscriptionState = {
  detail: {
    loading: false,
    error: null,
    data: {},
  },
  invoices: {
    loading: false,
    error: null,
    data: {},
  },
};

const Slice: sagaSlice = createSlice({
  name: "shopSubscription",
  initialState,
  reducers: {
    ...createSagaReducer("fetchShopSubscription", "detail"),
    ...createSagaReducer("fetchSubscriptionInvoices", "invoices"),
    /**
     * Writes a subscription straight into the detail entry, wrapped in the
     * envelope shape consumers already unwrap. Used after create, which
     * returns the full object, so the page updates without a second request.
     */
    updateShopSubscription(state, action) {
      state.detail.loading = false;
      state.detail.error = null;
      state.detail.data = { result: { data: action.payload } };
    },
  },
});

const fetchShopSubscriptionSaga = createAsyncSaga(
  "fetchShopSubscription",
  Slice,
  FetchShopSubscriptionService.fetchShopSubscription,
  ["shopId"]
);

const fetchSubscriptionInvoicesSaga = createAsyncSaga(
  "fetchSubscriptionInvoices",
  Slice,
  FetchSubscriptionInvoicesService.fetchSubscriptionInvoices,
  ["shopId", "page", "limit"]
);

addSaga(Slice.actions.fetchShopSubscription.type, fetchShopSubscriptionSaga);
addSaga(
  Slice.actions.fetchSubscriptionInvoices.type,
  fetchSubscriptionInvoicesSaga
);

export const {
  fetchShopSubscription,
  fetchSubscriptionInvoices,
  updateShopSubscription,
} = Slice.actions;

export default Slice.reducer;
